"use client";

import Link from "next/link";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";
import { m } from "motion/react";

type Variant = "primary" | "ghost";

type CommonProps = {
  variant?: Variant;
  trailingArrow?: boolean;
  className?: string;
  children: React.ReactNode;
};

type AsLink = CommonProps & {
  href: string;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps | "href">;

type AsButton = CommonProps & {
  href?: undefined;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps>;

export type ButtonProps = AsLink | AsButton;

const base =
  "inline-flex items-center gap-2 px-5 py-3 text-sm font-medium tracking-tight transition-colors duration-200 select-none";

const variants: Record<Variant, string> = {
  primary:
    "bg-[var(--color-blue)] text-[var(--color-navy)] hover:brightness-110",
  ghost:
    "border border-[var(--color-hairline)] text-[var(--color-text)] hover:border-[var(--color-hairline-strong)]",
};

function ButtonContent({
  children,
  trailingArrow,
}: {
  children: React.ReactNode;
  trailingArrow?: boolean;
}) {
  return (
    <>
      <span>{children}</span>
      {trailingArrow ? (
        <m.span
          aria-hidden
          initial={{ x: 0 }}
          whileHover={{ x: 4 }}
          transition={{ duration: 0.15 }}
        >
          →
        </m.span>
      ) : null}
    </>
  );
}

export function Button(props: ButtonProps) {
  const variant = props.variant ?? "primary";
  const className = [base, variants[variant], props.className ?? ""].join(" ");

  if ("href" in props && props.href) {
    const linkProps = props as AsLink;
    const {
      href,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured to exclude `variant`/`className` from `...rest` so they aren't spread onto the DOM element (className is applied explicitly, variant is invalid on <a>).
      variant: _v,
      trailingArrow,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- see above: keep className out of `...rest`.
      className: _c,
      children,
      ...rest
    } = linkProps;
    return (
      <Link href={href} className={className} {...rest}>
        <ButtonContent trailingArrow={trailingArrow}>{children}</ButtonContent>
      </Link>
    );
  }

  const buttonProps = props as AsButton;
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured to exclude `variant`/`className` from `...rest` so they aren't spread onto the DOM element (className is applied explicitly, variant is invalid on <button>).
    variant: _v,
    trailingArrow,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- see above: keep className out of `...rest`.
    className: _c,
    children,
    ...rest
  } = buttonProps;
  return (
    <button className={className} {...rest}>
      <ButtonContent trailingArrow={trailingArrow}>{children}</ButtonContent>
    </button>
  );
}
